import { Dexie } from 'dexie'
import * as sentry from '../../src/utils/sentry'
import type { FlowReport, FlowContinuation } from '../../../app-core/lib/flow/FlowReport'
import type { SyncProcedure } from '../../../app-core/lib/procedure/Procedure'
import type { User_UserProfile } from '../../../app/zap/User/User.IUser'
import type { Flow_ActionReport } from '../Flow/Flow.ActionReport'
import type { Imag_Photo } from '../Imag/Imag.Photo'
import type { Logg_Log } from '../Logg/Logg.Log'
import type { Noti_IDBNotification } from '../Noti/Noti.Notification'
import type { Post_TFile, Post_TFileId } from '../Post/Post.file'
import type { Post_TPost, Post_TPostId } from '../Post/Post.TPost'
import type { Proc_ContentPageTab } from '../Proc/Proc.ui.ContentPage'
import type { Rpts_OpenReport } from '../Rpts/Rpts.OpenReport'
import type { Rpts_ClientId } from '../Rpts/Rpts.types'
import type { Sync_Params } from '../Sync/Sync.standardSync'
import type { Team_Team } from '../Team/Team.Team'
import type { Todo_ConfirmedAction } from '../Todo/Todo.confirmScheduledAction'
import type { Todo_DoneReportDPO } from '../Todo/Todo.DoneReport'
import type { Todo_ScheduledInstanceDPO } from '../Todo/Todo.ScheduledInstance'
import type { Unit_ContentPageTab } from '../Unit/Unit.ui.ContentPage'
import type { PhotoFileName } from '../../../shared/domain/flows/types.no-deps'
import type { OncePer } from '../../../shared/domain/schedule/types.no-deps'
import type { DateString, UUID } from '../../../shared/lib/typeHelpers.no-deps'
import type { PointsAwardedEvent } from '../../../shared/domain/gamification/types.no-deps'
import type { Upload } from '../../../app-core/lib/upload/Upload'
import type { FileId, FileMetaApp } from '../../../shared/domain/files/types.no-deps'
import type { KnownUnit } from '../../../app-core/lib/unit/unit'
import type { ReportStatus } from '../../../shared/domain/reportStatus/types.no-deps'
import type { FeatureFlag } from '../../../shared/domain/status/types.no-deps'
import type { WorkflowReference, AppFlowWorkflow } from '../../../shared/domain/workflows/types.no-deps'
import type { Flow_Answer } from '../Flow/Flow.answer'
import type { Dictionary } from '../../../shared/lib/functional/dictionary.no-deps'
import type { ReportGuideDetails, SyncGuide } from '../../../shared/domain/guides'
import type { TrainingCompletionSync } from '../../../shared/domain/training/types.no-deps'
import { isNotNil } from '../../../shared/lib/functional/nil.no-deps'
import type { WorkflowFeedbackSync } from '../../../shared/domain/workflowFeedback/types.no-deps'
import type { Note_Note } from '../Note/Note.Note'
import type { ReportStatusFlow } from '../../../shared/domain/reportStatusFlow/types.no-deps'
// Documentation: http://dexie.org/docs

export let IDB_dexieDB: Dexie

export function IDB_dexieInitialise() {
    IDB_dexieDB = new Dexie('Zaptic', { chromeTransactionDurability: 'relaxed' })

    // http://dexie.org/docs/Version/Version.stores()#description

    IDB_dexieDB.version(1).stores({
        endpoints: 'name',
        posts: 'id, unit, [unit+location], procedure, fileId, status',
        files: 'fileId, url',
        fileContent: '',
        userProfiles: 'userId',
    })

    IDB_dexieDB.version(2).stores({
        lookup: '',
    })

    IDB_dexieDB.version(3).stores({
        openReports: '',
        doneReports: 'clientId, startDate, [scheduledInstance+startDate], dueDate, scheduledInstance',
        scheduledInstances: 'id, dueDate, schedule, [schedule+startDate], [schedule+dueDate]',
    })

    IDB_dexieDB.version(4).upgrade(async (_) => {
        const mimeType = 'application/pdf' // We only had pdfs so far
        await IDB_posts().each((post) => {
            if (post.contentType === 'file') {
                IDB_posts().update(post.id, { mimeType })
            }
        })

        await IDB_files().each((file) => IDB_files().update(file.fileId, { mimeType }))
    })

    IDB_dexieDB.version(5).stores({
        posts: 'id, unit, [unit+location], procedure, fileId, status, reportClientId',
    })

    IDB_dexieDB.version(6).stores({
        unsyncedConfirmedActions: 'clientId',
    })

    IDB_dexieDB.version(7).stores({
        scheduledInstances: 'id, dueDate',
        flowReports: 'id, status, completedDate',
        flowAnswers: 'id, report',
    })

    IDB_dexieDB.version(8).upgrade(async () => {
        const mapOncePer = (oldOncePer: string): OncePer => {
            switch (oldOncePer) {
                case 'oncePerTeam':
                    return 'onlyOnce'
                case 'oncePerUser':
                    return 'oncePerUser'
                default:
                    sentry.err('Bad once per value', { extra: { oncePer: oldOncePer } })
                    return 'oncePerUser'
            }
        }

        try {
            await IDB_scheduledInstances()
                .toCollection()
                .modify((scheduledInstance) => {
                    scheduledInstance.oncePer = mapOncePer(scheduledInstance.oncePer)
                })
        } catch (error) {
            sentry.err(error, {})
        }
    })

    IDB_dexieDB.version(9).stores({
        photos: 'fileName, status',
    })

    IDB_dexieDB.version(10).stores({
        flowActionReports: 'id, scheduledInstanceId, status',
    })

    //
    // Had to revert an app release, so these next few migrations are trying
    // to get us back into a stable state after some users have received divergent
    // migrations.

    IDB_dexieDB.version(11)
    IDB_dexieDB.version(12).stores({ procedures: null })
    IDB_dexieDB.version(13).stores({ procedures: 'id' })

    IDB_dexieDB.version(14).upgrade(async () => {
        // Resync flow workflows from the begining of time
        // As we now will get all of them with adHoc boolean
        await endpoints().delete('workflows/sync (Flow)')

        // Resync todos as now we want the action workflows
        await endpoints().delete('schedules/syncTodo')

        //
        // Remove {} scheduleInstance property's that should have been null

        const reports = await IDB_flowReports().toArray()

        const filteredFlowReports = reports
            .filter((flowReport) => JSON.stringify(flowReport.scheduledInstance) === JSON.stringify({}))
            .map((flowReport) => ({ ...flowReport, scheduledInstance: null }))

        await IDB_flowReports().bulkPut(filteredFlowReports)
    })

    IDB_dexieDB.version(15).upgrade(async () => {
        // Set all the sync lookups to default values
        await IDB_resetAllSyncParams()

        // Retreive all the old values
        const endpointRows = await endpoints().toArray()

        // Use them to set values in the new lookups
        await Promise.all(
            endpointRows.map((endpoint) => {
                const lastSyncTime = new Date(endpoint.lastSyncTime)
                const teams = endpoint.teams || []

                switch (endpoint.name) {
                    case 'posts.sync':
                        return IDB_syncParamsPosts().set({ lastSyncTime, teams })
                    case 'reports/syncDone':
                        return IDB_syncParamsDoneReports().set({ lastSyncTime, teams })
                    case 'schedules/syncTodo':
                        return IDB_syncParamsTodos().set({ lastSyncTime, teams })
                    case 'syncProcedures':
                        return IDB_syncParamsProcedures().set({ lastSyncTime, teams })
                    case 'user.syncProfiles':
                        return IDB_syncParamsUserProfiles().set({ lastSyncTime, teams })
                    case 'workflows.sync':
                        return IDB_syncParamsActivityWorkflows().set({ lastSyncTime, teams })
                    case 'workflows/sync (Flow)':
                        return IDB_syncParamsFlowWorkflows().set({ lastSyncTime, teams })
                }
            })
        )
    })

    IDB_dexieDB.version(16).stores({
        pointsAwardedEvents: 'time',
    })

    IDB_dexieDB.version(17).stores({
        uploads: ',groupId',
        fileChunks: '',
    })

    IDB_dexieDB.version(18)
        .stores({
            units: 'id, type',
        })
        .upgrade(async () => {
            await IDB_syncParamsUnits().reset()
        })

    IDB_dexieDB.version(19).upgrade(async () => {
        await IDB_syncParamsActivityWorkflows().reset()
        await IDB_syncParamsFlowWorkflows().reset()
    })

    IDB_dexieDB.version(20).stores({
        notifications: 'id, createdAt',
    })

    IDB_dexieDB.version(21)
        .stores({
            teams: 'id',
        })
        .upgrade(async () => {
            await IDB_syncParamsTeams().reset()
        })

    IDB_dexieDB.version(22)
        .stores({
            reportStatus: 'id, workflowId',
        })
        .upgrade(async () => {
            await IDB_syncParamsReportStatuses().reset()
        })

    IDB_dexieDB.version(23).stores({
        oldWorkflows: '[id+numberOfVersions]',
    })

    IDB_dexieDB.version(24).stores({
        flowReports: 'id, status, completedDate, progenitorClientId',
    })

    IDB_dexieDB.version(25).stores({
        photos: 'fileName, status, reportClientId',
    })

    IDB_dexieDB.version(26).stores({
        cachedImages: '',
    })

    IDB_dexieDB.version(27).upgrade(async () => {
        await IDB_syncParamsUserProfiles().reset()
    })

    IDB_dexieDB.version(28).stores({
        logs: '++logId, timestamp',
    })

    IDB_dexieDB.version(29).upgrade(async () => {
        await IDB_notifications()
            .filter((n) => n.type === ('schedule-reminder' as any))
            .delete()
    })

    IDB_dexieDB.version(30).stores({
        flowReports: 'id, status, startDate, completedDate, progenitorClientId',
        flowAnswers: 'id, report, [report+uploadState]',
    })

    IDB_dexieDB.version(31).stores({
        flowReports: 'id, status, startDate, completedDate, cancelledDate, unassignedDate, progenitorClientId',
    })

    IDB_dexieDB.version(32).stores({
        flowAnswers: 'id, report, uploadState, [report+uploadState]',
    })

    IDB_dexieDB.version(34).upgrade(async () => {
        // Fix issue where the wrong field name was accidentally used `reportId` -> `report`
        await IDB_posts()
            .orderBy('reportClientId')
            .modify((reportPost) => {
                const reportId: number = (reportPost as any).reportId

                if (reportPost.contentType === 'report' && reportPost.status === 'synced' && reportId !== null) {
                    reportPost.report = reportId
                }
            })

        // Fix issue where complete date was accidentally stored as number not date
        await IDB_flowReports()
            .orderBy('completedDate')
            .modify((flowReport) => {
                if (typeof flowReport.completedDate === 'number') {
                    flowReport.completedDate = new Date(flowReport.completedDate)
                }
            })
    })

    IDB_dexieDB.version(35).stores({
        filesMeta: 'fileId, fileName, fileSizeInBytes, extension, createdAt, status, fetchUrl',
    })

    IDB_dexieDB.version(36)
        .stores({
            guides: 'id',
        })
        .upgrade(async () => {
            await IDB_syncParamsGuides().reset()
        })

    IDB_dexieDB.version(37).upgrade(async () => {
        IDB_openReports()
            .toCollection()
            .modify((openReport) => {
                openReport.isAction = isNotNil(openReport.origin)
            })
        IDB_doneReports()
            .toCollection()
            .modify((doneReport) => {
                doneReport.isAction = isNotNil(doneReport.origin)
            })
        IDB_scheduledInstances()
            .toCollection()
            .modify((scheduledInstance) => {
                scheduledInstance.isAction = isNotNil(scheduledInstance.origin)
            })
    })

    IDB_dexieDB.version(38).upgrade(async () => {
        // Reset lastSyncTime to sync done reports that were not synced previously due to this bug - #11663
        await IDB_syncParamsDoneReports().reset()
    })

    IDB_dexieDB.version(39).stores({
        workflowFeedback: 'id',
    })

    IDB_dexieDB.version(40).stores({
        notes: 'id, status',
    })

    IDB_dexieDB.version(41).stores({
        scheduledInstances: 'id, dueDate, workflow',
    })

    IDB_dexieDB.version(42).stores({
        trainingCompletions: 'id',
    })

    /** team members added */
    IDB_dexieDB.version(43).upgrade(async () => {
        await IDB_syncParamsTeams().reset()
    })

    IDB_dexieDB.version(44).stores({
        notes: 'id, status, lastModified',
    })

    /** report guide details addeed */
    IDB_dexieDB.version(45).stores({
        reportGuideDetails: 'id',
    })

    IDB_dexieDB.version(46).stores({
        reportStatusFlow: 'id, workflow',
    })

    IDB_dexieDB.version(47).upgrade(async () => {
        await IDB_syncParamsTodos().reset()
        await IDB_syncParamsDoneReports().reset()
    })

    IDB_dexieDB.version(48).upgrade(async () => {
        await IDB_syncParamsUserProfiles().reset()

        // A new property was added to post data: `authorDeleted`.
        // Migration is to reset the sync param so that all posts are synced again.
        await IDB_syncParamsPosts().reset()
    })

    IDB_dexieDB.version(49).upgrade(async () => {
        // Reset lastSyncTime so that we can get latest training reports
        await IDB_syncParamsDoneReports().reset()
    })

    IDB_dexieDB.version(50).upgrade(async () => {
        // Reset users because team information was added
        await IDB_syncParamsUserProfiles().reset()
    })

    /**
     * Note when making a new migration. Make sure to consider whether the data your migrating
     * also lives in the IDB_deviceDB and the same migration needs to apply there.
     * eg. reports, posts, uploads, calendar events ...etc
     */
}

// Wrappers around dexieDB table in order to provide types

export const IDB_posts = () => IDB_dexieDB.table<Post_TPost, Post_TPostId>('posts')
export const IDB_files = () => IDB_dexieDB.table<Post_TFile, Post_TFileId>('files')
export const IDB_filesMeta = () => IDB_dexieDB.table<FileMetaApp, FileMetaApp['fileId']>('filesMeta')
export const IDB_fileContent = () => IDB_dexieDB.table<ArrayBuffer, Post_TFileId>('fileContent')
export const IDB_userProfiles = () => IDB_dexieDB.table<User_UserProfile, number>('userProfiles')
export const IDB_openReports = () => IDB_dexieDB.table<Rpts_OpenReport, string>('openReports')
export const IDB_doneReports = () => IDB_dexieDB.table<Todo_DoneReportDPO, Rpts_ClientId>('doneReports')
export const IDB_scheduledInstances = () => IDB_dexieDB.table<Todo_ScheduledInstanceDPO, UUID>('scheduledInstances')
export const IDB_flowActionReports = () =>
    IDB_dexieDB.table<Flow_ActionReport, Flow_ActionReport['id']>('flowActionReports')
// There could be existing reports with the entire workflow embedded and no version id
export const IDB_flowReports = <T extends WorkflowReference>() =>
    IDB_dexieDB.table<FlowReport<T>, FlowReport<T>['id']>('flowReports')
export const IDB_flowAnswers = () => IDB_dexieDB.table<Flow_Answer, Flow_Answer['id']>('flowAnswers')
export const IDB_pointsAwardedEvents = () => IDB_dexieDB.table<PointsAwardedEvent, Date>('pointsAwardedEvents')
export const IDB_procedures = () => IDB_dexieDB.table<SyncProcedure, number>('procedures')
export const IDB_units = () => IDB_dexieDB.table<KnownUnit, number>('units')
export const IDB_notifications = () => IDB_dexieDB.table<Noti_IDBNotification, UUID>('notifications')
export const IDB_teams = () => IDB_dexieDB.table<Team_Team, number>('teams')
export const IDB_reportStatus = () => IDB_dexieDB.table<ReportStatus, number>('reportStatus')
export const IDB_reportStatusFlow = () => IDB_dexieDB.table<ReportStatusFlow, number>('reportStatusFlow')
export const IDB_oldWorkflows = () => IDB_dexieDB.table<AppFlowWorkflow, number>('oldWorkflows')
export const IDB_logs = () => IDB_dexieDB.table<Logg_Log, number>('logs')
export const IDB_guides = () => IDB_dexieDB.table<SyncGuide, SyncGuide['id']>('guides')
export const IDB_workflowFeedback = () =>
    IDB_dexieDB.table<WorkflowFeedbackSync, WorkflowFeedbackSync['id']>('workflowFeedback')
export const IDB_notes = () => IDB_dexieDB.table<Note_Note, Note_Note['id']>('notes')
/**
 * Used for determining if any given training is complete.
 * Server is largely responsbile for providing these values, via sync,
 * but the App can mark a training as complete while offline.
 */
export const IDB_trainingCompletions = () =>
    IDB_dexieDB.table<TrainingCompletionSync, TrainingCompletionSync['id']>('trainingCompletions')
/**
 * Holds 'metadata' related to report guides (e.g. name).
 */
export const IDB_reportGuideDetails = () =>
    IDB_dexieDB.table<ReportGuideDetails, ReportGuideDetails['id']>('reportGuideDetails')

/**
 * @deprecated Use IDB_cachedImages from devicedb instead
 */
export const IDB_cachedImages = () => IDB_dexieDB.table<{ data: Blob }, string>('cachedImages')
/**
 * @deprecated Use IDB_uploads and IDB_fileChunks from devicedb instead
 */
export const IDB_photos = () => IDB_dexieDB.table<Imag_Photo, PhotoFileName>('photos')
/**
 * @deprecated Use IDB_uploads from devicedb instead
 */
export const IDB_uploads = () => IDB_dexieDB.table<Upload, FileId>('uploads')
/**
 * @deprecated Use IDB_fileChunks from devicedb instead
 */
export const IDB_fileChunks = () => IDB_dexieDB.table<{ data: ArrayBuffer }, string>('fileChunks')

//
// Create single key lookups

export const IDB_procedureContentPageTab = () => createLookup<Proc_ContentPageTab>('procedureContentPageTab')
export const IDB_unitContentPageTab = () => createLookup<Unit_ContentPageTab>('unitContentPageTab')
export const IDB_todoPageTab = () => createLookup<'todo' | 'done'>('todoPageTab')
export const IDB_homePage = () => createLookup<'todo' | 'search' | 'user-actions'>('homePage')
export const IDB_lastActivity = () => createLookup<Date>('lastActivity')
export const IDB_featureFlags = () => createLookup<FeatureFlag[]>('featureFlags')
export const IDB_flowContinuation = () => createLookup<FlowContinuation>('flowContinuation')
export const IDB_browserCameraDeviceIndex = () => createLookup<number>('browserCameraDeviceIndex')
export const IDB_slugs = () => createLookup<Dictionary<string>>('slugs')

function createLookup<T>(key: string) {
    return {
        get: (): Promise<T> => IDB_dexieDB.table('lookup').get(key),
        set: async (value: T) => {
            await IDB_dexieDB.table('lookup').put(value, key)
        },
    }
}

//
// SyncParams lookups

export const IDB_syncParamsActivityWorkflows = () => createSyncParamsLookup('syncParamsActivityWorkflows')
export const IDB_syncParamsDoneReports = () => createSyncParamsLookup('syncParamsDoneReports')
export const IDB_syncParamsFlowWorkflows = () => createSyncParamsLookup('syncParamsFlowWorkflows')
export const IDB_syncParamsNotifications = () => createSyncParamsLookup('syncParamsNotifications')
export const IDB_syncParamsPosts = () => createSyncParamsLookup('syncParamsPosts')
export const IDB_syncParamsProcedures = () => createSyncParamsLookup('syncParamsProcedures')
export const IDB_syncParamsTodos = () => createSyncParamsLookup('syncParamsTodos')
export const IDB_syncParamsTeams = () => createSyncParamsLookup('syncParamsTeams')
export const IDB_syncParamsUserProfiles = () => createSyncParamsLookup('syncParamsUserProfiles')
export const IDB_syncParamsUnits = () => createSyncParamsLookup('syncParamsUnits')
export const IDB_syncParamsReportStatuses = () => createSyncParamsLookup('syncParamsReportStatuses')
export const IDB_syncParamsReportStatusFlows = () => createSyncParamsLookup('syncParamsReportStatusFlows')
export const IDB_syncParamsGuides = () => createSyncParamsLookup('syncParamsGuides')
export const IDB_syncParamsNotes = () => createSyncParamsLookup('syncParamsNotes')
export const IDB_syncParamsTrainingCompletions = () => createSyncParamsLookup('syncParamsTrainingCompletions')
export const IDB_syncParamsReportGuideDetails = () => createSyncParamsLookup('syncParamsReportGuideDetails')

function createSyncParamsLookup(key: string) {
    const initialSyncParams: Sync_Params = { lastSyncTime: new Date(0), teams: [] }
    const { get, set } = createLookup<Sync_Params>(key)
    const reset = () => set(initialSyncParams)

    /*
     * Sometimes the sync params mysteriously disappear, so default to initial value (#4124)
     */
    const getWithDefault = () => get().then((params) => params ?? initialSyncParams)

    return { get: getWithDefault, set, reset }
}

export async function IDB_resetAllSyncParams() {
    await Promise.all([
        IDB_syncParamsActivityWorkflows().reset(),
        IDB_syncParamsDoneReports().reset(),
        IDB_syncParamsFlowWorkflows().reset(),
        IDB_syncParamsNotifications().reset(),
        IDB_syncParamsPosts().reset(),
        IDB_syncParamsProcedures().reset(),
        IDB_syncParamsTodos().reset(),
        IDB_syncParamsTeams().reset(),
        IDB_syncParamsUserProfiles().reset(),
        IDB_syncParamsUnits().reset(),
        IDB_syncParamsReportStatuses().reset(),
        IDB_syncParamsReportStatusFlows().reset(),
        IDB_syncParamsGuides().reset(),
        IDB_syncParamsNotes().reset(),
        IDB_syncParamsTrainingCompletions().reset(),
        IDB_syncParamsReportGuideDetails().reset(),
    ])
}

/**
 * DEPRECATED after version 2.0.20
 * Only here to sync old actions up after migration. New ones will be in flowActionReports
 */
export const IDB_unsyncedConfirmedActions = () =>
    IDB_dexieDB.table<Todo_ConfirmedAction, UUID>('unsyncedConfirmedActions')

/**
 * DEPRECATED
 * Endpoint table has been migrated to SyncParams lookups
 * This is just left for migrations that refer to it
 */

type LegacyEndpoint = { name: string; lastSyncTime: DateString | Date; teams?: number[] }
const endpoints = () => IDB_dexieDB.table<LegacyEndpoint, string>('endpoints')

//
// Helpers

/**
 * Slightly more typesafe update method that enforces that the changes are a partial of the type
 */
export function IDB_update<T, K>(table: Dexie.Table<T, K>, key: K, partial: Partial<T>) {
    return table.update(key, partial)
}
